/**
 * Click-and-drag horizontal scrolling for overflow-x containers.
 *
 * Opt in by adding `data-ttl-drag-scroll` to the scrolling element. Mouse only:
 * touch and pen keep native momentum scrolling, and the keyboard/trackpad paths
 * are untouched. A drag that moves past the threshold swallows the click that
 * follows, so dragging across a link doesn't navigate.
 */
(() => {
  const THRESHOLD = 4; // px of movement before a press counts as a drag

  const init = (scroller) => {
    if (scroller.dataset.ttlDragScrollReady) return;
    scroller.dataset.ttlDragScrollReady = 'true';

    let pointerId = null;
    let startX = 0;
    let startScrollLeft = 0;
    let dragging = false;
    let suppressClick = false;

    const endDrag = () => {
      if (pointerId !== null && scroller.hasPointerCapture(pointerId)) {
        scroller.releasePointerCapture(pointerId);
      }
      pointerId = null;
      scroller.classList.remove('ttl-drag-scroll--dragging');
    };

    scroller.addEventListener('pointerdown', (evt) => {
      // Always clear here: a press is what precedes a click, so a stale flag from
      // a drag that ended outside a link can never swallow a genuine click.
      suppressClick = false;

      if (evt.pointerType !== 'mouse' || evt.button !== 0) return;
      if (scroller.scrollWidth <= scroller.clientWidth) return;

      pointerId = evt.pointerId;
      startX = evt.clientX;
      startScrollLeft = scroller.scrollLeft;
      dragging = false;
    });

    scroller.addEventListener('pointermove', (evt) => {
      if (evt.pointerId !== pointerId) return;

      const dx = evt.clientX - startX;
      if (!dragging) {
        if (Math.abs(dx) < THRESHOLD) return;
        dragging = true;
        scroller.classList.add('ttl-drag-scroll--dragging');
        scroller.setPointerCapture(pointerId);
      }

      evt.preventDefault();
      scroller.scrollLeft = startScrollLeft - dx;
    });

    scroller.addEventListener('pointerup', () => {
      suppressClick = dragging;
      dragging = false;
      endDrag();
    });

    scroller.addEventListener('pointercancel', () => {
      dragging = false;
      endDrag();
    });

    // Swallow the click that a completed drag would otherwise fire on a link.
    scroller.addEventListener('click', (evt) => {
      if (!suppressClick) return;
      suppressClick = false;
      evt.preventDefault();
      evt.stopPropagation();
    }, true);

    // Native image dragging would hijack the gesture.
    scroller.addEventListener('dragstart', (evt) => evt.preventDefault());
  };

  const initAll = () => document.querySelectorAll('[data-ttl-drag-scroll]').forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Sections re-render in the theme editor without a page load.
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:block:select', initAll);
})();
